import { useFormContext } from 'react-hook-form';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { FormContainer } from 'components/layout';
import { Box, Typography } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Checkbox, ChipInput } from 'components/common';
import { useCampaignsData } from '../hooks';
import CreateCampaignButton from './CreateCampaignButton';

const CAMPAIGNS_LIMIT = 20;

const DataGridFooter = ({ selectedCount }: { selectedCount: number }) => {
  if (!selectedCount) return null;

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        height: 54,
        minHeight: 54,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        cursor: 'default',
      })}
    >
      {selectedCount === CAMPAIGNS_LIMIT ? (
        <>
          <InfoIcon color="primary" sx={{ fontSize: 21 }} />
          <Typography variant="body2">
            {`Up to ${CAMPAIGNS_LIMIT} campaigns can be selected`}
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          {selectedCount} rows selected
        </Typography>
      )}
    </Box>
  );
};

const DataGridCampaign = () => {
  const { watch, setValue, trigger } = useFormContext();
  const selectedCampaigns: string[] = watch('selectedCampaigns');

  const COLUMNS = useMemo(() => [
    { field: 'campaign_id', headerName: 'Campaign ID', width: 160 },
    { field: 'campaign_name', headerName: 'Campaign Name', width: 750 },
  ], []);

  const { gridRows, isLoading } = useCampaignsData();

  const handleSelectionMode = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      if (!gridRows) return;
      const parsedSelectedRows = selectedRows.slice(0, CAMPAIGNS_LIMIT);
      setValue('selectedCampaigns', parsedSelectedRows, { shouldValidate: true });
      setValue('selectedAdGroups', [], { shouldValidate: true });
    },
    [setValue, gridRows],
  );

  useEffect(() => {
    void trigger('selectedCampaigns');
  }, [trigger]);

  return (
    <Box style={{ height: 400, width: '100%' }}>
      <DataGrid
        rows={gridRows}
        columns={COLUMNS}
        checkboxSelection
        rowSelectionModel={selectedCampaigns}
        loading={isLoading}
        onRowSelectionModelChange={handleSelectionMode}
        hideFooterPagination
        keepNonExistentRowsSelected
        isRowSelectable={(params) =>
          selectedCampaigns.length < CAMPAIGNS_LIMIT ||
          selectedCampaigns.includes(params.row.campaign_id)
        }
        slots={{
          footer: () => <DataGridFooter selectedCount={selectedCampaigns.length} />,
        }}
        getRowId={(params) => params.campaign_id}
      />
    </Box>
  );
};

const ChooseCampaign: FC = memo(() => {
  const { formState: { errors } } = useFormContext();
  const isCampaignFieldError = errors?.selectedCampaigns?.message as string;

  return (
    <FormContainer
      title={'Choose Campaigns'}
      error={isCampaignFieldError}
      sx={{ flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'nowrap',
          }}
        >
          <ChipInput
            name="filterCampaignsByName"
            inputLabel="Enter campaign name"
          />
          <Checkbox
            dataTestId={'selectedCampaignsOnly'}
            name={'selectedCampaignsOnly'}
            label={'Selected only'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <CreateCampaignButton />
        </Box>
      </Box>
      <DataGridCampaign />
    </FormContainer>
  );
});
ChooseCampaign.displayName = 'ChooseCampaign';

export default ChooseCampaign;